import { Box, Tooltip } from '@mui/material'
import React from 'react'
import Button from './Button'
import CheckBox from './CheckBox'
import TextField from './TextField'
import Txt from './Txt'
import { OnboardingType, usePdfDownload } from '../queries/getPdf'
import { findLegalForm } from '../utils/company'
import { Restaurant } from '../utils/restaurant'

// Die Dokumente des Onboardings in der Reihenfolge, in der sie im gemeinsamen PDF stehen.
const ONBOARDING_DOCUMENT: Record<OnboardingType, { label: string; fileName: string }> = {
	PARTNER_CONTRACT: { label: 'Dienstleistungsvertrag', fileName: 'Dienstleistungsvertrag' },
	DEVICE_CONTRACT: { label: 'Empfangsbestätigung Endgerät', fileName: 'Empfangsbestaetigung' },
	CHECKLIST: { label: 'Checkliste', fileName: 'Checkliste' },
	MENU_STICKER: { label: 'Aufkleber Speisekarte', fileName: 'Aufkleber' },
}

const DOCUMENT_TYPES = Object.keys(ONBOARDING_DOCUMENT) as OnboardingType[]
const DEFAULT_DOCUMENTS: OnboardingType[] = ['PARTNER_CONTRACT', 'CHECKLIST', 'MENU_STICKER']

const sxColumn = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: '1rem',
	width: '100%',
} as const

const sxActions = {
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: '1rem',
	width: '100%',
} as const

// Je Abschnitt ein umrandeter Block mit Kopfzeile; die Zeilen trennt eine Linie.
const sxSegment = {
	width: '100%',
	maxWidth: '48rem',
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
	overflow: 'hidden',
} as const

const sxSegmentHeader = {
	px: '1rem',
	py: '0.5rem',
	bgcolor: 'action.hover',
	color: 'text.secondary',
	fontSize: '0.75rem',
	fontWeight: 'bold',
	textTransform: 'uppercase',
	letterSpacing: '0.05em',
} as const

const sxSegmentRow = {
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: '1rem',
	minHeight: '4.5rem',
	px: '1rem',
	py: '0.75rem',
	borderTop: '1px solid',
	borderColor: 'divider',
} as const

// Feste Breite, damit die Felder rechts untereinander bündig stehen.
const sxLeftCell = { width: '18rem', flexShrink: 0 } as const

type Props = {
	restaurant?: Restaurant | null
	// Ohne Restaurantdaten, alle Angaben zum Händler werden von Hand eingetragen (Tab Dokumente).
	blank?: boolean
}

// Die Dokumente im Guide: je Dokument ein Haken, alle angehakten kommen in ein PDF, das vor Ort
// ausgedruckt und unterschrieben wird.
const OnboardingDocumentsForm = ({ restaurant, blank }: Props): JSX.Element => {
	const [documents, setDocuments] = React.useState<OnboardingType[]>(DEFAULT_DOCUMENTS)
	const [deviceDescription, setDeviceDescription] = React.useState('Sunmi V2 Pro')
	const [duplicate, setDuplicate] = React.useState(true)
	const { downloadPdf, loading } = usePdfDownload()
	const withDevice = documents.includes('DEVICE_CONTRACT')

	// Ohne vollständigen Vertragskopf (Parteien- und Restaurantblock) taugt kein Dokument, außer blanko.
	const company = restaurant?.company
	const missingHeaderFields = [
		{ label: 'Firmenname', value: company?.name },
		{ label: 'Rechtsform', value: company?.legalForm },
		{ label: 'Geschäftsführer/Inhaber', value: company?.managingDirector },
		{ label: 'Firmen-Straße', value: company?.street },
		{ label: 'Firmen-Hausnummer', value: company?.houseNumber },
		{ label: 'Firmen-PLZ', value: company?.zipCode },
		{ label: 'Firmen-Stadt', value: company?.city },
		{ label: 'USt-IdNr. oder Steuernummer', value: company?.ustId || company?.taxId },
		{
			label: 'Handelsregisternr.',
			value:
				company?.tradeRegisterNumber || findLegalForm(company?.legalForm)?.hasRegister === false,
		},
		{ label: 'Restaurant-Straße', value: restaurant?.street },
		{ label: 'Restaurant-Hausnummer', value: restaurant?.houseNumber },
		{ label: 'Restaurant-PLZ', value: restaurant?.zipCode },
		{ label: 'Restaurant-Stadt', value: restaurant?.city },
	]
		.filter((field) => !blank && !field.value)
		.map((field) => field.label)

	// Die Reihenfolge bleibt die des gemeinsamen PDFs, egal in welcher angehakt wird.
	const handleDocumentToggle = React.useCallback((checked: boolean, id?: string) => {
		setDocuments((current) =>
			DOCUMENT_TYPES.filter((document) => (document === id ? checked : current.includes(document)))
		)
	}, [])

	const handleDeviceDescriptionChange = React.useCallback((value: string | number) => {
		setDeviceDescription(String(value))
	}, [])

	const handleDownload = React.useCallback(async () => {
		if (blank || restaurant) {
			await downloadPdf({
				restaurantId: blank ? null : restaurant?.id ?? null,
				documents,
				deviceDescription: withDevice && !blank ? deviceDescription.trim() : null,
				duplicate,
				fileName: `${blank ? 'Blanko_' : ''}${documents
					.map((document) => ONBOARDING_DOCUMENT[document].fileName)
					.join('_')}.pdf`,
			})
		}
	}, [blank, downloadPdf, restaurant, documents, withDevice, deviceDescription, duplicate])

	return (
		<Box sx={sxColumn}>
			<Box sx={sxSegment}>
				<Box sx={sxSegmentHeader}>Dokumente</Box>
				{DOCUMENT_TYPES.map((document) => (
					<Box key={document} sx={sxSegmentRow}>
						<Box sx={sxLeftCell}>
							<CheckBox
								id={document}
								label={ONBOARDING_DOCUMENT[document].label}
								defaultValue={documents.includes(document)}
								onChange={handleDocumentToggle}
							/>
						</Box>
						{document === 'MENU_STICKER' ? (
							<Txt variant="body2" color="text.secondary" m="0">
								{blank
									? 'A4-Bogen mit sechs Etiketten 105 × 99 mm für die Papierkarte'
									: 'A4-Bogen mit acht Etiketten 105 × 74 mm für die Papierkarte'}
							</Txt>
						) : document === 'CHECKLIST' ? (
							<Txt variant="body2" color="text.secondary" m="0">
								Für den Termin beim Restaurant, nicht zum Unterschreiben
							</Txt>
						) : document === 'PARTNER_CONTRACT' ? (
							<Txt variant="body2" color="text.secondary" m="0">
								Unterschreiben Restaurant und Beyer Development
							</Txt>
						) : document === 'DEVICE_CONTRACT' && blank ? (
							<Txt variant="body2" color="text.secondary" m="0">
								Gerät und Seriennummer werden von Hand eingetragen
							</Txt>
						) : document === 'DEVICE_CONTRACT' && withDevice ? (
							<>
								<TextField
									label="Gerätebeschreibung"
									defaultValue={deviceDescription}
									onChange={handleDeviceDescriptionChange}
									variant="outlined"
									width="24rem"
								/>
								{deviceDescription.trim() ? null : (
									<Txt variant="body2" color="error.main" m="0">
										Bitte das überlassene Gerät angeben.
									</Txt>
								)}
							</>
						) : null}
					</Box>
				))}
			</Box>
			{missingHeaderFields.length ? (
				<Txt variant="body1" color="error.main" m="0">
					Für den Vertragskopf fehlt: {missingHeaderFields.join(', ')}.
				</Txt>
			) : null}
			<Box sx={sxActions}>
				<Tooltip
					title="Dienstleistungsvertrag und Empfangsbestätigung je zweimal im PDF – ein Exemplar fürs Restaurant, eins für Beyer Development"
					arrow
				>
					<Box>
						<CheckBox label="Doppelte Ausführung" defaultValue={duplicate} onChange={setDuplicate} />
					</Box>
				</Tooltip>
				<Button
					variant="outlined"
					startIconName="PictureAsPdfOutlined"
					onClick={handleDownload}
					loading={loading}
					disabled={
						(!blank && !restaurant) ||
						!documents.length ||
						(withDevice && !blank && !deviceDescription.trim()) ||
						missingHeaderFields.length > 0
					}
				>
					PDF zum Ausdrucken
				</Button>
			</Box>
		</Box>
	)
}

export default React.memo(OnboardingDocumentsForm)
